import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import SmartValuesPanel from '@/components/SmartValuesPanel.vue'
import {
  extractEmailHtmlDocument,
  scanEmailHtmlCompatibility,
  findUnsafeEmailHtml,
} from '@/util/emailHtml'


export default /*#__PURE__*/_defineComponent({
  __name: 'HtmlBlockDrawer',
  props: {
    show: { type: Boolean },
    block: null
  },
  emits: ['close', 'update'],
  setup(__props: any, { emit }) {

const props = __props as {
  show: boolean
  block: { html?: string; hoistedStyles?: string } | null
}





const editor = ref<HTMLTextAreaElement | null>(null)
const previewFrame = ref<HTMLIFrameElement | null>(null)
const htmlDraft = ref('')
const stylesDraft = ref('')
const pasteNotices = ref<string[]>([])
const showSmartValues = ref(false)
const showDiscardConfirm = ref(false)
const previewHeight = ref(160)

const isDirty = computed(() =>
  htmlDraft.value !== ((props.block && props.block.html) || '') ||
  stylesDraft.value !== ((props.block && props.block.hoistedStyles) || ''))

const unsafeMessages = computed(() => findUnsafeEmailHtml(htmlDraft.value, stylesDraft.value))
const compatibilityWarnings = computed(() => scanEmailHtmlCompatibility(htmlDraft.value, stylesDraft.value))

const sizeLabel = computed(() => {
  const bytes = new Blob([htmlDraft.value]).size
  if (bytes < 1024) return `${bytes} B`
  return `${Math.round(bytes / 1024)} KB`
})

const previewSrcdoc = computed(() =>
  `<!doctype html><html><head><style>body{margin:0;padding:0}</style><style>${stylesDraft.value}</style></head><body>${htmlDraft.value}</body></html>`)

watch(() => props.show, visible => {
  if (visible) {
    htmlDraft.value = (props.block && props.block.html) || ''
    stylesDraft.value = (props.block && props.block.hoistedStyles) || ''
    pasteNotices.value = []
    showSmartValues.value = false
    showDiscardConfirm.value = false
  }
})

watch(previewSrcdoc, () => {
  requestAnimationFrame(() => measurePreview())
})

function measurePreview() {
  const el = previewFrame.value
  if (!el || !el.contentDocument || !el.contentDocument.body) return
  previewHeight.value = Math.min(600, Math.max(160, el.contentDocument.body.scrollHeight))
}

function onPaste() {
  // Let the paste land first, then normalize the full value.
  nextTick(() => {
    const extracted = extractEmailHtmlDocument(htmlDraft.value)
    if (extracted.notices.length === 0) return
    htmlDraft.value = extracted.html
    if (extracted.hoistedStyles) {
      stylesDraft.value = stylesDraft.value
        ? `${stylesDraft.value}\n${extracted.hoistedStyles}`
        : extracted.hoistedStyles
    }
    pasteNotices.value = extracted.notices
  })
}

function insertSmartValue(value: { syntax: string }) {
  const el = editor.value
  const syntax = value && value.syntax ? value.syntax : ''
  if (!el || !syntax) return
  const start = el.selectionStart != null ? el.selectionStart : htmlDraft.value.length
  const end = el.selectionEnd != null ? el.selectionEnd : start
  htmlDraft.value = htmlDraft.value.slice(0, start) + syntax + htmlDraft.value.slice(end)
  nextTick(() => {
    el.focus()
    el.setSelectionRange(start + syntax.length, start + syntax.length)
  })
}

function save() {
  if (unsafeMessages.value.length > 0) return
  emit('update', { html: htmlDraft.value, hoistedStyles: stylesDraft.value })
  emit('close')
}

function onCloseRequest() {
  if (isDirty.value) {
    showDiscardConfirm.value = true
    return
  }
  emit('close')
}

function discardAndClose() {
  showDiscardConfirm.value = false
  emit('close')
}

// BaseDrawer does not handle ESC; each consumer adds its own listener.
function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && props.show) {
    if (showDiscardConfirm.value) {
      showDiscardConfirm.value = false
      return
    }
    onCloseRequest()
  }
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))

return { __sfc: true,props, emit, editor, previewFrame, htmlDraft, stylesDraft, pasteNotices, showSmartValues, showDiscardConfirm, previewHeight, isDirty, unsafeMessages, compatibilityWarnings, sizeLabel, previewSrcdoc, measurePreview, onPaste, insertSmartValue, save, onCloseRequest, discardAndClose, onKeydown, SmartValuesPanel }
}

})